// =====================================================================
// Détail du calcul de pénalité FIS (FIS Points Rules 2026/27, art. 4.4),
// présenté comme la feuille officielle « PENALTY CALCULATION » :
//   - Best 10 at Finish : 10 premiers classés (ex aequo au 10e inclus,
//     art. 4.4.5), les 5 meilleurs points FIS marqués « Best 5 » avec
//     leurs points de course ;
//   - Best 5 FIS Points Started : 5 meilleurs points FIS parmi les partants
//     (classés, DSQ, DNF, DNS 2e manche — pas DNS1 / NPS1, art. 4.4.2) ;
//   - A, B, C, (A+B−C)/10, arrondi (art. 4.4.8), adder de catégorie,
//     valeur de correction Z, pénalité calculée, pénalité appliquée
//     (minimum de catégorie).
// Points FIS manquants ou supérieurs au maximum -> maximum (art. 4.4.3/4) ;
// points de course plafonnés au maximum (art. 4.4.7) ; 5e meilleur ex
// aequo -> celui qui a le plus de points de course (art. 4.4.6).
// Fonction pure, utilisée par FisPanel.jsx (affichage) et le PDF.
// =====================================================================

export const MAX_VALUE = { DH: 330, SG: 270, SL: 165, GS: 220, AC: 270, SC: 270 };

// Télémark (règles FIS points, art. 1715) : même calcul qu'en alpin avec
// F = 500 et maximum 200 pour GS, CL et SP.
export const TM_FACTORS = { GS: { f: 500, max: 200 }, CL: { f: 500, max: 200 }, SP: { f: 500, max: 200 } };

// Points de course : P = (Tx / To − 1) × F, sur les temps tronqués au 1/100,
// arrondis au centième.
export function racePointsFromTimes(ranking, f, timeOf) {
    const cs = (ms) => Math.floor(Math.round(ms) / 10);
    const valid = ranking.filter((r) => timeOf(r) != null);
    if (!valid.length) return [];
    const t0 = Math.min(...valid.map((r) => cs(timeOf(r))));
    return valid.map((r) => ({
        bib: r.bib,
        name: r.name,
        rank: r.rank,
        points: Math.round(((cs(timeOf(r)) / t0 - 1) * f + Number.EPSILON) * 100) / 100,
    }));
}

const num = (v) => {
    const n = parseFloat(String(v ?? "").replace(",", "."));
    return Number.isFinite(n) ? n : null;
};
// Arrondi art. 4.4.8 : 4 millièmes vers le bas, 5 vers le haut.
export const roundPenalty = (x) => Math.round((x + Number.EPSILON * Math.sign(x)) * 100) / 100;

/**
 * @param classified [{ rank, bib, fiscode, name, nation, fisPoints }]   classés (ordre du classement)
 * @param starters   [{ bib, fiscode, name, nation, fisPoints, status, rank }] partants (classés + DSQ/DNF/DNS2)
 * @param racePointsByBib { bib: points de course }
 * @param discipline "GS" | "SL" | …
 * @param options { categoryAdder, correctionZ, minimumPenalty }
 */
export function computePenaltyDetail({ classified, starters, racePointsByBib, discipline, categoryAdder = 0, correctionZ = 0, minimumPenalty = null, maxValue = null }) {
    // maxValue : imposé par le secteur (télémark 200), sinon table alpine.
    const max = maxValue ?? MAX_VALUE[String(discipline || "").toUpperCase()] ?? 220;
    const capFis = (p) => (p == null || p > max ? max : p);
    const raceOf = (bib) => {
        const p = num(racePointsByBib?.[String(bib)]);
        return p == null ? null : Math.min(p, max);
    };

    // --- Best 10 at finish (ex aequo au 10e compris) ---
    const sorted = [...classified].sort((a, b) => (a.rank ?? 9999) - (b.rank ?? 9999));
    const tenthRank = sorted[9]?.rank;
    const best10 = sorted.filter((r, i) => i < 10 || (tenthRank != null && r.rank === tenthRank));
    const rows10 = best10.map((r) => ({
        ...r,
        fisPointsUsed: capFis(num(r.fisPoints)),
        racePoints: raceOf(r.bib),
        missingPoints: num(r.fisPoints) == null,
    }));
    // 5 meilleurs points FIS ; égalité -> plus de points de course (4.4.6)
    const pickA = [...rows10]
        .sort((a, b) => a.fisPointsUsed - b.fisPointsUsed || (b.racePoints ?? 0) - (a.racePoints ?? 0))
        .slice(0, 5);
    const pickASet = new Set(pickA.map((r) => String(r.bib)));
    rows10.forEach((r) => (r.inBest5 = pickASet.has(String(r.bib))));
    const sumA = pickA.reduce((s, r) => s + r.fisPointsUsed, 0) + Math.max(0, 5 - pickA.length) * max;
    const sumC = pickA.reduce((s, r) => s + (r.racePoints ?? max), 0) + Math.max(0, 5 - pickA.length) * max;

    // --- Best 5 FIS points started ---
    const startedRows = (starters || []).map((r) => ({ ...r, fisPointsUsed: capFis(num(r.fisPoints)), missingPoints: num(r.fisPoints) == null }));
    const pickB = [...startedRows].sort((a, b) => a.fisPointsUsed - b.fisPointsUsed).slice(0, 5);
    const sumB = pickB.reduce((s, r) => s + r.fisPointsUsed, 0) + Math.max(0, 5 - pickB.length) * max;

    const raw = (sumA + sumB - sumC) / 10;
    const rounded = roundPenalty(raw);
    const adder = num(categoryAdder) ?? 0;
    const z = num(correctionZ) ?? 0;
    const calculated = roundPenalty(rounded + adder + z);

    // Moins de 3 coureurs avec points FIS dans les 5 retenus, ou au moins 3
    // classés sans points : minimum = double du maximum (4.4.4).
    const withPointsInA = pickA.filter((r) => !r.missingPoints && num(r.fisPoints) <= max).length;
    const classifiedWithout = classified.filter((r) => num(r.fisPoints) == null).length;
    const doubleMaxRule = withPointsInA < 3 || classifiedWithout >= 3;

    const minPen = num(minimumPenalty);
    let applied = calculated;
    // Une pénalité appliquée n'est jamais négative ; minimum de catégorie
    // éventuel par-dessus.
    if (applied < 0) applied = 0;
    if (minPen != null && applied < minPen) applied = minPen;
    if (doubleMaxRule && applied < 2 * max) applied = 2 * max;

    return {
        max,
        best10: rows10,
        best5Started: pickB,
        sumA,
        sumB,
        sumC,
        raw,
        rounded,
        categoryAdder: adder,
        correctionZ: z,
        calculated,
        minimumPenalty: minPen,
        doubleMaxRule,
        applied: roundPenalty(applied),
    };
}